import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ViewportService } from './viewport.service';

const EDGE_ZONE_PX = 24;
const DIRECTION_THRESHOLD_PX = 8;
const SWIPE_MIN_DISTANCE_PX = 30;

@Injectable({
  providedIn: 'root',
})
export class GestureNavigationService {
  private initialized = false;
  private enabled = true;
  private edgeTouchActive = false;
  private directionDecided = false;
  private isHorizontal = false;
  private startX = 0;
  private startY = 0;
  private startedFromLeft = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly location: Location,
    private readonly viewport: ViewportService,
  ) {}

  initEdgeNavigation(): void {
    if (this.initialized) return;
    if (this.viewport.isNativeIos() || this.viewport.isNativeAndroid()) return;
    if (!this.viewport.isNativePlatform() && !this.viewport.isMobileDevice())
      return;

    const body = this.document?.body;
    if (!body) return;

    body.addEventListener('touchstart', this.onTouchStart, {
      passive: true,
      capture: true,
    });
    body.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
      capture: true,
    });
    body.addEventListener('touchend', this.onTouchEnd, {
      passive: true,
      capture: true,
    });
    this.initialized = true;
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
  }

  destroy(): void {
    const body = this.document?.body;
    if (!body) return;
    body.removeEventListener('touchstart', this.onTouchStart, {
      capture: true,
    });
    body.removeEventListener('touchmove', this.onTouchMove, { capture: true });
    body.removeEventListener('touchend', this.onTouchEnd, { capture: true });
    this.initialized = false;
  }

  private resetTouchState(): void {
    this.edgeTouchActive = false;
    this.directionDecided = false;
    this.isHorizontal = false;
  }

  private isEdgeZone(x: number): boolean {
    return x <= EDGE_ZONE_PX || x >= window.innerWidth - EDGE_ZONE_PX;
  }

  private onTouchStart = (e: TouchEvent): void => {
    this.resetTouchState();

    if (!this.enabled || e.touches.length !== 1) return;

    const { clientX: x, clientY: y } = e.touches[0];
    if (!this.isEdgeZone(x)) return;

    const target = e.target as HTMLElement | null;
    if (
      target?.closest?.(
        '.bottom-sheet-overlay, .cdk-overlay-container, ui-mobile-drawer',
      )
    ) {
      return;
    }

    this.edgeTouchActive = true;
    this.startX = x;
    this.startY = y;
    this.startedFromLeft = x <= EDGE_ZONE_PX;
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (!this.edgeTouchActive || e.touches.length !== 1) return;

    if (!this.directionDecided) {
      const dx = Math.abs(e.touches[0].clientX - this.startX);
      const dy = Math.abs(e.touches[0].clientY - this.startY);

      if (dx < DIRECTION_THRESHOLD_PX && dy < DIRECTION_THRESHOLD_PX) return;

      this.directionDecided = true;
      this.isHorizontal = dx > dy;

      if (!this.isHorizontal) {
        this.edgeTouchActive = false;
        return;
      }
    }

    e.preventDefault();
  };

  private onTouchEnd = (e: TouchEvent): void => {
    if (!this.edgeTouchActive || !this.isHorizontal) {
      this.resetTouchState();
      return;
    }

    const endX = e.changedTouches?.[0]?.clientX ?? this.startX;
    const deltaX = endX - this.startX;

    if (this.startedFromLeft && deltaX >= SWIPE_MIN_DISTANCE_PX) {
      this.location.back();
    } else if (!this.startedFromLeft && deltaX <= -SWIPE_MIN_DISTANCE_PX) {
      this.location.forward();
    }

    this.resetTouchState();
  };
}
